import { useMemo, useState } from "react";
import { useClickOutside } from "../utils/useClickOutside";
import styles from "./MultiSelectFilter.module.css";
import { Chevron } from "./Chevron";

// Cap rendered matches so a large org catalogue doesn't render a giant
// list; the operator narrows with the query instead.
const MAX_VISIBLE = 50;

interface Props {
  // The selectable values, in the order shown.
  options: string[];
  // Currently-selected values (original casing).
  selected: string[];
  onChange: (next: string[]) => void;
  // What one value is and what several are, for the button and the search box.
  noun?: { one: string; many: string };
  // How a value reads, where it is an id rather than a name.
  labelOf?: (value: string) => string;
  // Off for a short list, where a search box is clutter.
  searchable?: boolean;
  // Compact sits in a dense filter bar (the dashboard's), at its smaller type.
  size?: "regular" | "compact";
}

const ORGANISATIONS = { one: "organisation", many: "organisations" };

// Multi-select dropdown over a flat list. The button summarises the selection;
// opening it reveals a search box + checkbox list.
export const MultiSelectFilter = ({
  options,
  selected,
  onChange,
  noun = ORGANISATIONS,
  labelOf = (value) => value,
  searchable = true,
  size = "regular",
}: Props) => {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const wrapRef = useClickOutside<HTMLDivElement>(open, () => setOpen(false));

  const selectedKeys = useMemo(
    () => new Set(selected.map((s) => s.toLowerCase())),
    [selected],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q
      ? options.filter((o) => labelOf(o).toLowerCase().includes(q))
      : options;
  }, [options, query, labelOf]);

  const visible = filtered.slice(0, MAX_VISIBLE);
  // While searching, "Select all" adds the matches in view that are not ticked yet.
  const unpicked = query.trim()
    ? visible.filter((o) => !selectedKeys.has(o.toLowerCase()))
    : [];

  const toggle = (org: string) => {
    const key = org.toLowerCase();
    if (selectedKeys.has(key)) {
      onChange(selected.filter((s) => s.toLowerCase() !== key));
    } else {
      onChange([...selected, org]);
    }
  };

  const summary =
    selected.length === 0
      ? `All ${noun.many}`
      : selected.length === 1
        ? labelOf(selected[0])
        : `${selected.length} ${noun.many}`;

  return (
    <div
      className={`${styles.wrap} ${size === "compact" ? styles.compact : ""}`}
      ref={wrapRef}
    >
      <button
        type="button"
        className={styles.trigger}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        disabled={options.length === 0}
        title={
          options.length === 0 ? `No ${noun.many} yet` : `Filter by ${noun.one}`
        }
      >
        <span className={styles.triggerLabel}>{summary}</span>
        <span className={styles.caret}>
          <Chevron open={open} />
        </span>
      </button>

      {open && (
        <div className={styles.listbox} role="listbox" aria-multiselectable>
          {searchable && (
            <input
              type="text"
              className={styles.search}
              placeholder={`Search ${noun.many}…`}
              value={query}
              autoFocus
              onChange={(e) => setQuery(e.target.value)}
            />
          )}
          {(unpicked.length > 0 || selected.length > 0) && (
            <div className={styles.links}>
              {unpicked.length > 0 && (
                <button
                  type="button"
                  className={styles.link}
                  onClick={() => onChange([...selected, ...unpicked])}
                >
                  Select all
                </button>
              )}
              {selected.length > 0 && (
                <button
                  type="button"
                  className={styles.link}
                  onClick={() => onChange([])}
                >
                  Clear selection ({selected.length})
                </button>
              )}
            </div>
          )}
          <div className={styles.options}>
            {visible.length === 0 ? (
              <div className={styles.empty}>No matching {noun.many}.</div>
            ) : (
              visible.map((org) => {
                const checked = selectedKeys.has(org.toLowerCase());
                return (
                  <label
                    key={org}
                    className={styles.option}
                    role="option"
                    aria-selected={checked}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(org)}
                    />
                    <span className={styles.optionName}>{labelOf(org)}</span>
                  </label>
                );
              })
            )}
            {filtered.length > MAX_VISIBLE && (
              <div className={styles.more}>
                Showing first {MAX_VISIBLE} of {filtered.length}. Keep typing to
                narrow.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
